import { useGetList, useNotify, useTranslate } from "ra-core";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MISSED_CALL_TEMPLATE_SLUG } from "@/components/gavion/domain/email";
import { useDialerApi } from "../dialer/api/DialerApiContext";

const K = "gavion.notifications.missed_call";
const LINK =
  "inline-flex min-h-11 items-center text-sm font-medium text-[#B8500C] underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F77119]";

type TemplateRow = { id: number | string; language: string };

/**
 * GAVION rep-missed-call: the rep's own « Appel manqué » email switch (Profil). Stored in sales.prefs.missed_call_email
 * through set_my_missed_call_email (57_gavion_rep_missed_call.sql); absent means OFF (owner decision 2026-10-15): the
 * send_email executor sends R25's email for this rep's calls only once he switched it on (else `rep_switch_off`).
 * « Modifier le courriel » opens the template.
 */
export const MissedCallPrefCard = () => {
  const api = useDialerApi();
  const t = useTranslate();
  const notify = useNotify();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const { data: templates = [] } = useGetList<TemplateRow>("email_templates", {
    filter: { slug: MISSED_CALL_TEMPLATE_SLUG },
    pagination: { page: 1, perPage: 10 },
  });
  const fr = templates.find((r) => r.language === "fr");
  const en = templates.find((r) => r.language === "en");

  useEffect(() => {
    let live = true;
    api.getMyPrefs().then(
      (p) => live && setEnabled(p.missed_call_email === true),
      () => live && setEnabled(false),
    );
    return () => {
      live = false;
    };
  }, [api]);

  const onChange = async (next: boolean) => {
    const previous = enabled;
    setEnabled(next);
    setBusy(true);
    try {
      const prefs = await api.setMyMissedCallEmail(next);
      setEnabled(prefs.missed_call_email === true);
      notify(`${K}.${next ? "on" : "off"}`, { type: "success" });
    } catch {
      setEnabled(previous);
      notify(`${K}.error`, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {t(`${K}.title`)}
        </h2>
        <div className="flex min-h-11 flex-wrap items-center gap-3">
          <Switch
            id="gavion-missed-call-email"
            checked={enabled === true}
            disabled={enabled === null || busy}
            aria-describedby="gavion-missed-call-email-help"
            onCheckedChange={onChange}
          />
          <Label htmlFor="gavion-missed-call-email">{t(`${K}.toggle`)}</Label>
        </div>
        <p
          id="gavion-missed-call-email-help"
          className="text-sm text-muted-foreground"
        >
          {t(`${K}.help`)}
        </p>
        <div className="flex flex-wrap gap-x-4">
          <Link
            to={fr ? `/courriels/modeles/${fr.id}` : "/courriels/modeles"}
            className={LINK}
          >
            {t(`${K}.edit`)}
          </Link>
          {en && (
            <Link to={`/courriels/modeles/${en.id}`} className={LINK}>
              {t(`${K}.edit_en`)}
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
